<template>
  <GenericCard
    :locked="locked"
    :subtitle="subtitle"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Passport"
    :section="section"
    has-footer
    has-documents
    :document-types="documentTypes"
    :document-identifier="documentIdentifier"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <!-- autocomplete country_id:countryOptions -->
        <v-col
          cols="4"
        >
          <v-autocomplete
            v-model="section.country_id"
            :items="countryOptions"
            item-text="name"
            item-value="id"
            label="Passport Country*"
            outlined
            :error-messages="getErrorMessages($v.section.country_id)"
            @input="$v.section.country_id.$touch()"
            @blur="$v.section.country_id.$touch()"
          />
        </v-col>

        <!-- text passport_number -->
        <v-col
          cols="4"
        >
          <v-text-field
            v-model="section.passport_number"
            autocomplete="off"
            label="Passport Number*"
            outlined
            :error-messages="getErrorMessages($v.section.passport_number)"
            @input="$v.section.passport_number.$touch()"
            @blur="$v.section.passport_number.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <!-- combo-date issue_date -->
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Issue Date"
            :formatable-date="section.issue_date"
            :format="section.issue_date_format"
            :uncertain="section.issue_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.issue_date)"
            @update-box="onIssueDateUncertainUpdate"
            @update-date="onIssueDateUpdate"
            @tab-out="$v.section.issue_date.$touch()"
          />
        </v-col>

        <!-- text place_issued -->
        <v-col
          cols="4"
        >
          <v-text-field
            v-model="section.place_issued"
            autocomplete="off"
            label="Place Issued"
            outlined
            :error-messages="getErrorMessages($v.section.place_issued)"
            @input="$v.section.place_issued.$touch()"
            @blur="$v.section.place_issued.$touch()"
          />
        </v-col>

        <!-- combo-date expiry_date -->
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Expiry Date"
            :formatable-date="section.expiry_date"
            :format="section.expiry_date_format"
            :uncertain="section.expiry_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.expiry_date)"
            @update-box="onExpiryDateUncertainUpdate"
            @update-date="onExpiryDateUpdate"
            @tab-out="$v.section.expiry_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages($v.section.notes)"
            @input="$v.section.notes.$touch()"
            @blur="$v.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'PassportCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      documentTypes: [
        { text: 'Passport Document', value: 'passport-document' },
      ],

      section: {},
    }
  },

  validations () {
    return {
      section: {
        country_id: { required: helpers.withMessage('Country is Required', required) },
        expiry_date: {},
        expiry_date_uncertain: {},
        issue_date: {},
        issue_date_uncertain: {},
        notes: {},
        passport_number: { required: helpers.withMessage('Passport Number is Required', required) },
        place_issued: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `passport-card-${this.sectionIdentifier}` },
    countryOptions () { return this.getCachedDropdown('countries') },
    create () { return this.item.id === null },
    documentIdentifier () { return `details-passport-${this.section.id}` },

    locked: {
      get () {
        return this.pePassportCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPePassportCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.countryOptions?.find(a => a.id === this.section.country_id)?.name} - ${this.section.passport_number})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'pePassportCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPePassportCard(this.sectionIdentifier)

    this.getDropdownOptions({ options: false, path: 'countries' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.pePassportCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.pePassportCardCreateUndo()
      else {
        return this.pePassportCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.pePassportCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.pePassportCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    // on-expiry_date update
    onExpiryDateUncertainUpdate (value) {
      this.$v.section.expiry_date.$touch()
      this.$v.section.expiry_date_uncertain.$touch()
      this.section.expiry_date_uncertain = value
    },

    onExpiryDateUpdate (value) {
      this.$v.section.expiry_date.$touch()
      this.$v.section.expiry_date_uncertain.$touch()
      this.section.expiry_date = value.date
      this.section.expiry_date_format = value.format
    },

    // on-issue_date update
    onIssueDateUncertainUpdate (value) {
      this.$v.section.issue_date.$touch()
      this.$v.section.issue_date_uncertain.$touch()
      this.section.issue_date_uncertain = value
    },

    onIssueDateUpdate (value) {
      this.$v.section.issue_date.$touch()
      this.$v.section.issue_date_uncertain.$touch()
      this.section.issue_date = value.date
      this.section.issue_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPePassportCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'pePassportCardCreateUndo',
      'setPePassportCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPePassportCard',
      'pePassportCardCreate',
      'pePassportCardDelete',
      'pePassportCardRefresh',
      'pePassportCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
